import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import { getUpcomingHubEvents, getReplayPostUrl } from '../../services/hubEvents.service';
import { buildGoogleCalendarAddUrl } from '../../utils/googleCalendarLink';
import api from '../../services/api'; // Join/replay clicks count on the Sales & Marketing page (2026-09-28)

/** Format a Hub event's date/time for display, in the member's own timezone. */
const formatEventTime = (event) => {
  const raw = event.event_datetime || event.event_date || event.scheduled_date;
  if (!raw) return null;
  const d = new Date(raw);
  const hasTime = event.event_datetime != null;
  return d.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(hasTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {}),
  });
};

export default function EventsAndReplays() {
  const [events, setEvents] = useState(null);
  const [replayUrl, setReplayUrl] = useState(null);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null); // event whose full description is showing

  useEffect(() => {
    Promise.allSettled([getUpcomingHubEvents(), getReplayPostUrl()]).then(([ev, replay]) => {
      if (ev.status === 'fulfilled') setEvents(ev.value);
      else setError('Could not load upcoming events.');
      if (replay.status === 'fulfilled') setReplayUrl(replay.value?.url || null);
    });
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <PageHeading title="Events & Replays" subtitle="Live Hub events, and anything you missed." />
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        )}

        <div className="flex justify-end mb-4">
          <Link to="/dashboard/replays" className="text-xs text-gray-400 hover:text-reset-terracotta transition">
            Missed a class? Watch replays →
          </Link>
        </div>

        {replayUrl && (
          <a
            href={replayUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { api.post('/members/me/engagement', { type: 'replay_watch', ref: 'Older replays' }).catch(() => {}); }}
            className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-6 hover:bg-gray-100 transition group"
          >
            <span className="text-xl">▶️</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Older Replays</p>
              <p className="text-xs text-gray-400">From before this week</p>
            </div>
            <span className="text-xs text-gray-400 group-hover:text-gray-600 transition">Watch →</span>
          </a>
        )}

        <h2 className="text-sm font-bold text-reset-brown mb-3">Upcoming events</h2>

        {!events ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            No upcoming events scheduled right now — check back soon.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-50">
            {events.map(ev => (
              <div key={ev.ghl_event_id} className="flex items-center gap-3 px-5 py-4">
                {ev.type_image_url ? (
                  <img src={ev.type_image_url} alt={ev.type_image_alt_text || ev.title} className="w-20 aspect-video rounded-lg object-contain shrink-0 bg-reset-terracotta/10" />
                ) : (
                  <img src="/events-icon.png" alt="" className="w-5 h-5 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 leading-snug truncate">{ev.title}</p>
                  {formatEventTime(ev) && (
                    <p className="text-xs text-gray-400 mt-0.5">{formatEventTime(ev)}</p>
                  )}
                  {/* Two lines, then "more" for the whole description (Jenn, 2026-10-01). */}
                  {ev.description && (
                    <p className={`text-xs text-gray-500 mt-1 whitespace-pre-line ${openId === ev.ghl_event_id ? '' : 'line-clamp-2'}`}>{ev.description}</p>
                  )}
                  {ev.description && ev.description.length > 90 && (
                    <button
                      type="button"
                      onClick={() => setOpenId(openId === ev.ghl_event_id ? null : ev.ghl_event_id)}
                      aria-expanded={openId === ev.ghl_event_id}
                      className="text-xs text-reset-terracotta hover:underline mt-0.5"
                    >
                      {openId === ev.ghl_event_id ? 'less' : 'more'}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {ev.event_datetime && (
                    <a
                      href={buildGoogleCalendarAddUrl({ title: ev.title, start: ev.event_datetime, details: ev.event_url })}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Add to Google Calendar"
                      className="text-xs text-gray-400 border border-gray-200 px-2.5 py-1.5 rounded-full hover:border-reset-terracotta hover:text-reset-terracotta transition"
                    >
                      + Calendar
                    </a>
                  )}
                  {ev.event_url && (
                    <a
                      href={ev.event_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs bg-reset-terracotta text-white px-3 py-1.5 rounded-full hover:bg-reset-brown transition font-medium"
                    >
                      Join →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
